import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { deleteCampaignDraft } from "@/api/campaignBuilder";
import {
  archiveCampaign,
  cancelCampaign,
  disableCampaign,
  enableCampaign,
  fetchCancelPreview,
  pauseCampaign,
  restoreCampaign,
  resumeCampaign,
} from "@/api/campaigns";
import { useCampaignEndpoints } from "@/context/CampaignEndpointsContext";
import { invalidateCampaignReads } from "@/utils/campaignCache-helper";

/** New send/expiry dates supplied when resuming a campaign whose window lapsed while paused. */
export interface ResumeDates {
  scheduledDate?: string | null;
  expireDate?: string | null;
}

/**
 * The campaign lifecycle mutations behind the card's action menu — pause / resume / cancel / archive /
 * restore (un-archive) / delete. Each invalidates the campaign reads on success, so the card re-renders
 * with its new DERIVED status (the services only write an overlay column). Callers own the dialogs,
 * toasts and error copy; this hook is just the wiring.
 */
export function useCampaignLifecycle(campaignId: string) {
  const queryClient = useQueryClient();
  const onSuccess = () => invalidateCampaignReads(queryClient);

  const pause = useMutation({ mutationFn: () => pauseCampaign(campaignId), onSuccess });
  const resume = useMutation({
    mutationFn: (dates?: ResumeDates) => resumeCampaign(campaignId, dates),
    onSuccess,
  });
  const cancel = useMutation({ mutationFn: () => cancelCampaign(campaignId), onSuccess });
  const archive = useMutation({ mutationFn: () => archiveCampaign(campaignId), onSuccess });
  const restore = useMutation({ mutationFn: () => restoreCampaign(campaignId), onSuccess });
  const remove = useMutation({ mutationFn: () => deleteCampaignDraft(campaignId), onSuccess });
  // Universal Link only: turn the shared /u/ link off/on.
  const disable = useMutation({ mutationFn: () => disableCampaign(campaignId), onSuccess });
  const enable = useMutation({ mutationFn: () => enableCampaign(campaignId), onSuccess });

  return { pause, resume, cancel, archive, restore, remove, disable, enable };
}

/**
 * The Cancel dialog's "what happens to the money" breakdown, fetched when the dialog opens
 * (`enabled`). A dry-run on the server, so it never changes anything by being shown.
 */
export function useCancelPreview(campaignId: string, enabled: boolean) {
  const path = useCampaignEndpoints().cancelPreview(campaignId);
  return useQuery({
    queryKey: ["cancelPreview", campaignId, path],
    queryFn: () => fetchCancelPreview(campaignId, path),
    enabled,
    staleTime: 0,
  });
}
